import { useEffect, useRef, useState } from "react";
import { jobMembersProfile, IRoomGroupParams } from "../../endpoints/job.service";
import { IMessageJobMembersProfile } from "../../endpoints/types/message.type";

type JobOption = { _id: string; title: string; companyName?: string; referenceId?: string };
const emptyPagination = { hasNextPage: false, nextCursor: null as string | null };
const emptyCounts = { totalCount: 0, filteredCount: 0, unreadCount: 0 };

export default function useConversationGroups(params: IRoomGroupParams, revision: unknown) {
  const [groups, setGroups] = useState<IMessageJobMembersProfile[]>([]);
  const [jobPosts, setJobPosts] = useState<JobOption[]>([]);
  const [counts, setCounts] = useState(emptyCounts);
  const [pagination, setPagination] = useState(emptyPagination);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const generation = useRef(0);
  const paging = useRef(false);
  const loadedPages = useRef(1);
  const hasLoaded = useRef(false);
  const previousKey = useRef<string | undefined>(undefined);
  const failedNextPage = useRef(false);
  const previousRefresh = useRef(refresh);
  const key = JSON.stringify(params);

  useEffect(() => {
    const requestId = ++generation.current;
    let cancelled = false;
    const queryChanged = previousKey.current !== key;
    const manualRefresh = previousRefresh.current !== refresh;
    const background = hasLoaded.current && !queryChanged;
    previousKey.current = key;
    previousRefresh.current = refresh;
    if (queryChanged) {
      loadedPages.current = 1;
      hasLoaded.current = false;
      setGroups([]);
      setJobPosts([]);
      setCounts(emptyCounts);
      setPagination(emptyPagination);
    }
    const pageCount = loadedPages.current;
    paging.current = false;
    setLoading(!background);
    setRefreshing(background);
    setLoadingMore(false);
    setError("");
    failedNextPage.current = false;

    const fetchPages = async () => {
      try {
        const merged = new Map<string, IMessageJobMembersProfile>();
        let cursor: string;
        let data: any;
        let pagesRead = 0;
        // Refresh the loaded window atomically, without dropping expanded rows
        // from previously loaded pages or leaving stale filtered results behind.
        for (let page = 0; page < pageCount; page++) {
          const response = await jobMembersProfile({ ...JSON.parse(key), ...(cursor ? { cursor } : {}) });
          if (cancelled || requestId !== generation.current) return;
          if (!response?.success) throw new Error("Unable to load conversations.");
          data = response.data;
          for (const group of data?.data || []) merged.set(group._id, group);
          pagesRead++;
          cursor = data?.pagination?.nextCursor;
          if (!data?.pagination?.hasNextPage || !cursor) break;
        }
        hasLoaded.current = true;
        loadedPages.current = pagesRead;
        setGroups([...merged.values()]);
        setJobPosts(data?.jobPosts || []);
        setCounts(data?.counts || emptyCounts);
        setPagination(data?.pagination || emptyPagination);
      } catch {
        if (!cancelled && requestId === generation.current) setError("Unable to load conversations. Please try again.");
      } finally {
        if (!cancelled && requestId === generation.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    // Receive + seen notifications often update Redux within the same burst.
    // Coalesce those refreshes; initial loads and user filter changes stay immediate.
    const timer = background && !manualRefresh ? setTimeout(fetchPages, 200) : undefined;
    if (timer === undefined) void fetchPages();
    return () => { cancelled = true; clearTimeout(timer); ++generation.current; };
  }, [key, revision, refresh]);

  const loadMore = async () => {
    if (loading || refreshing || paging.current || !pagination.hasNextPage || !pagination.nextCursor) return;
    const requestId = generation.current;
    paging.current = true;
    setLoadingMore(true);
    setError("");
    failedNextPage.current = false;
    try {
      const response = await jobMembersProfile({ ...JSON.parse(key), cursor: pagination.nextCursor });
      if (requestId !== generation.current) return;
      if (!response?.success) throw new Error("Unable to load conversations.");
      loadedPages.current++;
      setGroups((current) => {
        const merged = new Map(current.map((group) => [group._id, group]));
        for (const group of response.data?.data || []) merged.set(group._id, group);
        return [...merged.values()];
      });
      setPagination(response.data?.pagination || emptyPagination);
      setCounts(response.data?.counts || emptyCounts);
    } catch {
      if (requestId === generation.current) {
        failedNextPage.current = true;
        setError("Unable to load more conversations. Please try again.");
      }
    } finally {
      if (requestId === generation.current) {
        paging.current = false;
        setLoadingMore(false);
      }
    }
  };

  return { groups, jobPosts, counts, pagination, loading, refreshing, loadingMore, error, loadMore,
    retryFailed: () => failedNextPage.current ? loadMore() : setRefresh((value) => value + 1),
    retry: () => setRefresh((value) => value + 1) };
}
